import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, Search, ExternalLink, MessageCircle, Ticket } from 'lucide-react';
import { useSupportChat } from '@/app/support-chat';
import { config } from '@/lib/config';
import { HELP_TOPICS } from '@/content/help';
import { usePublicHelpCatalog } from '@/lib/use-public-help';
import { workspaceHelpResults, type WorkspaceSearchOption } from '@/lib/workspace-help-search';
import { NAV_SECTIONS } from './app-shell';
import { Input } from '@/components/ui/form';
import { Modal } from '@/components/ui/system';
/**
 * `chat` opens the support chat instead of a page, with its text (if any) in the message box; `ticket` opens the
 * chat's ticket form.
 */
type CommandOption = WorkspaceSearchOption & { chat?: string; ticket?: boolean };
const CHAT_WORDS = ['chat with support', 'contact support', 'ask the assistant'];
// The team is reached through a ticket, so asking for a person finds that.
const TICKET_WORDS = ['raise a support ticket', 'support ticket', 'report a problem', 'contact support', 'talk to a person'];
const said = (words: string[], query: string) => words.some((w) => w.includes(query.toLowerCase()));
export function CommandMenu({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const catalog = usePublicHelpCatalog(open);
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const results = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const chat = useSupportChat();
  useEffect(() => {
    if (open) {
      setText('');
      setActive(0);
    }
  }, [open]);
  useEffect(() => {
    if (open)
      results.current
        ?.querySelector<HTMLElement>(`[data-active="true"]`)
        ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);
  const options = useMemo<CommandOption[]>(() => {
    const query = text.trim();
    const pages = [
      ...NAV_SECTIONS.flatMap((s) => s.items).map((n) => ({ label: n.label, to: n.to, section: 'Pages' })),
      { label: 'Team performance', to: '/?view=team', section: 'Reports' },
      { label: 'Missed opportunities', to: '/?view=missed', section: 'Reports' },
      { label: 'Busy hours', to: '/?view=hours', section: 'Reports' },
      { label: 'Help & support', to: '/help', section: 'Pages' },
    ];
    const matches = pages.filter((n) => n.label.toLowerCase().includes(query.toLowerCase()));
    const help = workspaceHelpResults(query, catalog, HELP_TOPICS, config.helpUrl);
    const talk: CommandOption[] = chat
      ? [
          ...(said(CHAT_WORDS, query) ? [{ label: 'Chat with support', to: '#chat', section: 'Help', chat: '' }] : []),
          ...(said(TICKET_WORDS, query)
            ? [{ label: 'Raise a support ticket', to: '#ticket', section: 'Help', ticket: true }]
            : []),
        ]
      : [];
    return query
      ? [
          ...matches,
          ...talk,
          ...help,
          {
            label: `Search calls for “${query}”`,
            to: `/calls?range=all&q=${encodeURIComponent(query)}`,
            section: 'Search',
          },
          {
            label: `Search contacts for “${query}”`,
            to: `/contacts?q=${encodeURIComponent(query)}`,
            section: 'Search',
          },
          ...(chat
            ? [{ label: `Ask support about “${query}”`, to: '#ask', section: 'Help', chat: query }]
            : []),
        ]
      : [...matches, ...talk, ...help];
  }, [text, catalog, chat]);
  useEffect(() => {
    setActive((index) => Math.min(index, Math.max(0, options.length - 1)));
  }, [options.length]);
  const go = (option: CommandOption, index: number) => {
    if (option.ticket) {
      onOpenChange(false);
      chat?.openTicket();
      return;
    }
    if (option.chat !== undefined) {
      onOpenChange(false);
      chat?.open(option.chat || undefined);
      return;
    }
    if (option.external) {
      document.getElementById(`${listId}-${index}`)?.click();
      return;
    }
    onOpenChange(false);
    navigate(option.to);
  };
  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Search workspace"
      description="Find calls, contacts, pages or help. Help Center guides open in a new tab."
    >
      <div className="command-search">
        <Search size={16} />
        <Input
          autoFocus
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={options[active] ? `${listId}-${active}` : undefined}
          aria-label="Search workspace"
          aria-description="Use the keyboard Up and Down arrow keys to choose a result, then press Enter to open it."
          placeholder="Search calls, contacts, pages or help…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, options.length - 1));
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(0, i - 1));
            }
            if (e.key === 'Enter' && options[active]) go(options[active], active);
          }}
        />
      </div>
      <div className="command-results" id={listId} ref={results} role="listbox" aria-label="Search results">
        {options.map((o, i) => {
          const props = {
            id: `${listId}-${i}`,
            role: 'option',
            'aria-selected': i === active,
            'data-active': i === active,
            onMouseEnter: () => setActive(i),
          };
          const content = (
            <>
              <div>
                <span>{o.label}</span>
                <small>
                  {o.section}
                  {o.external ? ' · Opens in a new tab' : ''}
                </small>
              </div>
              {o.external ? (
                <ExternalLink size={14} />
              ) : o.ticket ? (
                <Ticket size={14} />
              ) : o.chat !== undefined ? (
                <MessageCircle size={14} />
              ) : (
                <ArrowRight size={14} />
              )}
            </>
          );
          return o.external ? (
            <a
              {...props}
              key={o.to}
              href={o.to}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onOpenChange(false)}
            >
              {content}
            </a>
          ) : (
            <button {...props} key={o.to} onClick={() => go(o, i)}>
              {content}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
